import { useState } from "react";
import { Phone, Mail, MapPin, Languages, Flag, Users, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { useT } from "../i18n";
import StatusBadge from "./StatusBadge";
import ReportDialog from "./ReportDialog";
import ContactDialog from "./ContactDialog";

const LANG_LABELS = { fr: "FR", gcf: "Kreyòl", en: "EN" };
const CARD_INITIAL = { opacity: 0, y: 16 };
const CARD_ANIMATE = { opacity: 1, y: 0 };

export default function ProCard({ pro, index }) {
  const { t, lang } = useT();
  const [reportOpen, setReportOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const locale = lang === "en" ? "en-GB" : "fr-FR";
  const cardTransition = { duration: 0.35, delay: Math.min(index * 0.05, 0.4) };
  const updated = new Date(pro.status_updated_at).toLocaleDateString(locale, {
    day: "numeric", month: "long", year: "numeric",
  });

  return (
    <motion.article
      data-testid={`pro-card-${pro.id}`}
      className="bg-white rounded-xl border border-black/5 p-6 flex flex-col gap-4 h-full transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(13,64,69,0.06)]"
      initial={CARD_INITIAL}
      animate={CARD_ANIMATE}
      transition={cardTransition}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-lg tracking-tight text-[#1F2937]" style={{ fontFamily: "Manrope" }}>
            {pro.name}
            {pro.is_example && (
              <span data-testid={`example-badge-${pro.id}`} className="ml-2 align-middle text-[10px] uppercase tracking-[0.15em] font-semibold text-[#92400E] bg-[#FEF3C7] border border-[#FBBF24] rounded-full px-2 py-0.5">
                {t("card.example")}
              </span>
            )}
          </h3>
          <p className="text-xs uppercase tracking-[0.15em] text-[#0D4045] font-semibold mt-1">
            {t(`specialty.${pro.specialty}`)}
          </p>
        </div>
        <StatusBadge status={pro.status} testid={`pro-card-status-${pro.id}`} />
      </div>

      <div className="flex items-center gap-4 text-sm text-[#6B7280]">
        <span className="flex items-center gap-1.5"><MapPin size={14} /> {pro.commune}</span>
        {pro.languages?.length > 0 && (
          <span className="flex items-center gap-1.5">
            <Languages size={14} /> {pro.languages.map((l) => LANG_LABELS[l] || l).join(" · ")}
          </span>
        )}
      </div>

      {(pro.places_available != null || pro.wait_estimate) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold text-[#0D4045]">
          {pro.places_available != null && (
            <span data-testid={`pro-card-places-${pro.id}`} className="flex items-center gap-1.5">
              <Users size={14} /> {t("card.places", { a: pro.places_available })}
              {pro.places_total != null && ` / ${pro.places_total}`}
            </span>
          )}
          {pro.wait_estimate && (
            <span data-testid={`pro-card-wait-${pro.id}`} className="flex items-center gap-1.5">
              <Clock size={14} /> {t("card.wait", { w: pro.wait_estimate })}
            </span>
          )}
        </div>
      )}

      {pro.note && <p className="text-sm text-[#1F2937]/80 leading-relaxed">{pro.note}</p>}

      <div className="mt-auto pt-2 border-t border-[#0D4045]/10 flex flex-col gap-3">
        <p data-testid={`pro-card-updated-${pro.id}`} className="text-xs font-semibold text-[#0D4045]">
          {t("card.updated")} {updated}
        </p>
        <div className="flex flex-wrap gap-2">
          {pro.phone && (
            <a href={`tel:${pro.phone.replace(/\s/g, "")}`} data-testid={`pro-card-call-btn-${pro.id}`}
              className="flex items-center gap-1.5 bg-[#0D4045] text-[#FDFBF7] text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-[#0a3237] transition-colors duration-200">
              <Phone size={15} /> {t("card.call")}
            </a>
          )}
          {pro.contact_email && (
            <button onClick={() => setContactOpen(true)} data-testid={`pro-card-contact-btn-${pro.id}`}
              className="flex items-center gap-1.5 bg-[#E8F3F1] text-[#0D4045] text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-[#d7eae6] transition-colors duration-200">
              <Mail size={15} /> {t("card.contact")}
            </button>
          )}
          <button onClick={() => setReportOpen(true)} data-testid={`pro-card-report-btn-${pro.id}`}
            className="flex items-center gap-1.5 text-sm font-medium text-[#6B7280] px-3 py-2.5 rounded-full hover:bg-black/5 transition-colors duration-200">
            <Flag size={14} /> {t("card.report")}
          </button>
        </div>
      </div>

      <ReportDialog pro={pro} open={reportOpen} onOpenChange={setReportOpen} />
      <ContactDialog pro={pro} open={contactOpen} onOpenChange={setContactOpen} />
    </motion.article>
  );
}
